// scheduledByPost — small helper for the My Creatives page. Fetches the
// user's tile-attached scheduled rows via GET /api/scheduler/posts-by-
// source and groups them by post_id so each post card can render its
// own status pill.
//
// Phase 4: routed through the backend (Cloud SQL). Pre-Phase-4 this hit
// Supabase directly under RLS — removed when the auth layer migrated to
// Firebase + we decommissioned supabase-js.

import { API_BASE, getAuthHeaders } from './api';
import type { ScheduledPost } from './api';

/** Fetch scheduled tiles for the current user, grouped by post_id.
 *
 *  Both kinds of "tile-attached" rows show up here:
 *    - mode='ready_post'    → post_id points at an existing My Creatives row
 *    - mode='auto_generate' → generated_post_id points at a row inserted
 *                             by the Slice B worker
 *
 *  The map key is whichever id is set. Placeholder auto_generate tiles
 *  (neither id set — pre-worker) are filtered out by the backend, since
 *  they don't yet correspond to a My Creatives row.
 */
export async function fetchScheduledByPost(): Promise<Record<string, ScheduledPost[]>> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE}/api/scheduler/posts-by-source`, {
      headers: await getAuthHeaders(),
    });
  } catch (err) {
    console.error('[scheduledByPost] network error', err);
    return {};
  }
  if (!response.ok) {
    console.error('[scheduledByPost] HTTP', response.status);
    return {};
  }
  const data = (await response.json()) as ScheduledPost[];
  if (!Array.isArray(data)) return {};

  const map: Record<string, ScheduledPost[]> = {};
  for (const row of data) {
    const key = row.post_id || row.generated_post_id;
    if (!key) continue;
    (map[key] ||= []).push(row);
  }
  return map;
}

/** What status a post card should show. Computed from the post's scheduled
 *  tiles (if any). Priority (top wins when tiles span multiple states):
 *  posted (≥1 success) → failed (no success, ≥1 failure) → scheduled
 *  (≥1 still pending) → none. Failed sits above scheduled because the
 *  user needs to see it; once they see a failure they can act on it
 *  (delete + reschedule, fix the connection, etc.). */
export type PostScheduleStatus =
  | { kind: 'none' }
  | {
      kind: 'scheduled'
      /** Next future tile across all platforms. */
      next: ScheduledPost
      /** Unique platforms this post is scheduled on (in the future). */
      platforms: string[]
      /** Total upcoming tiles across all platforms. */
      count: number
    }
  | {
      kind: 'posted'
      /** Most recent posted tile. */
      latest: ScheduledPost
      /** Unique platforms this post has been published to. */
      platforms: string[]
    }
  | {
      kind: 'failed'
      /** Most recent failed tile — surfaces its error message in the pill. */
      latest: ScheduledPost
      /** Unique platforms whose publish attempt failed. */
      platforms: string[]
      /** Total failed tiles. */
      count: number
    };

export function summarisePostSchedule(tiles: ScheduledPost[] | undefined): PostScheduleStatus {
  if (!tiles || tiles.length === 0) return { kind: 'none' };

  const posted = tiles.filter((t) => t.status === 'posted');
  if (posted.length > 0) {
    // Sort by posted_at desc, fall back to scheduled_at.
    const latest = [...posted].sort((a, b) => {
      const aT = a.posted_at || a.scheduled_at;
      const bT = b.posted_at || b.scheduled_at;
      return bT.localeCompare(aT);
    })[0];
    const platforms = Array.from(new Set(posted.map((t) => t.platform)));
    return { kind: 'posted', latest, platforms };
  }

  // Failed wins over scheduled because the user needs to act on it. (Pure
  // 'pending' tiles in the past shouldn't exist post-Phase-3 — the publish
  // worker either posts them or marks them failed within ~1 min — but we
  // keep the upcoming check below to handle the gap pre-deploy + any
  // weird clock skew.)
  const failed = tiles.filter((t) => t.status === 'failed');
  if (failed.length > 0) {
    const latest = [...failed].sort((a, b) =>
      (b.scheduled_at).localeCompare(a.scheduled_at),
    )[0];
    const platforms = Array.from(new Set(failed.map((t) => t.platform)));
    return { kind: 'failed', latest, platforms, count: failed.length };
  }

  const now = new Date();
  const upcoming = tiles.filter(
    (t) =>
      (t.status === 'pending' || t.status === 'generating' || t.status === 'publishing') &&
      new Date(t.scheduled_at) > now,
  );
  if (upcoming.length > 0) {
    const next = [...upcoming].sort((a, b) => a.scheduled_at.localeCompare(b.scheduled_at))[0];
    const platforms = Array.from(new Set(upcoming.map((t) => t.platform)));
    return { kind: 'scheduled', next, platforms, count: upcoming.length };
  }

  // Tiles exist but they're all in the past + not posted + not failed
  // (cancelled edge case). Treat as 'none' on the gallery surface; the
  // calendar page renders the explicit cancellation.
  return { kind: 'none' };
}

/** Pick the "edit cohort" — the set of upcoming tiles that share the same
 *  scheduled_at as the next-upcoming one. This mirrors how create-mode
 *  fan-out works (one timestamp, N platform rows), so editing the cohort
 *  as a group lets the user manage that whole multi-platform schedule
 *  in one modal. Tiles at OTHER times (e.g. "IG Mon 9am" AND "FB Tue 5pm"
 *  on the same post) stay out of the cohort and are managed via /schedule
 *  individually — avoids the surprising "moved IG also moved FB" pitfall. */
export function editCohortTiles(tiles: ScheduledPost[] | undefined): ScheduledPost[] {
  const summary = summarisePostSchedule(tiles);
  if (summary.kind !== 'scheduled' || !tiles) return [];
  const targetTime = summary.next.scheduled_at;
  return tiles.filter(
    (t) =>
      t.scheduled_at === targetTime &&
      (t.status === 'pending' || t.status === 'generating' || t.status === 'publishing'),
  );
}
